"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useViewQueryParamsSync = void 0;
const react_1 = require("react");
const ramda_1 = require("ramda");
const react_router_1 = require("react-router");
const view_query_params_models_1 = require("./view-query-params.models");
const view_query_params_utils_1 = require("./view-query-params.utils");
const getSearchWithViewState = (search, queryKey, encodedState) => {
    const searchParams = new URLSearchParams(search);
    if (encodedState) {
        searchParams.set(queryKey, encodedState);
    }
    else {
        searchParams.delete(queryKey);
    }
    const nextSearch = searchParams.toString();
    return nextSearch ? `?${nextSearch}` : '';
};
const useViewQueryParamsSync = ({ viewState, defaults, onHydrate, shouldHydrate, enabled = true, queryKey = view_query_params_models_1.VIEW_QUERY_STATE_QUERY_KEY, }) => {
    const { hash, pathname, search } = (0, react_router_1.useLocation)();
    const navigate = (0, react_router_1.useNavigate)();
    const hydratedStateRef = (0, react_1.useRef)();
    const pendingHydrationRef = (0, react_1.useRef)();
    const writtenQueryKeyRef = (0, react_1.useRef)();
    const pendingWriteQueryKeyRef = (0, react_1.useRef)();
    const pendingWriteSourceQueryKeyRef = (0, react_1.useRef)();
    const encodedQueryState = (0, react_1.useMemo)(() => new URLSearchParams(search).get(queryKey), [queryKey, search]);
    const encodedViewState = (0, react_1.useMemo)(() => (0, view_query_params_utils_1.encodeViewQueryState)(viewState, defaults), [defaults, viewState]);
    const hydrationKey = enabled ? encodedQueryState !== null && encodedQueryState !== void 0 ? encodedQueryState : '' : 'disabled';
    const [hydratedQueryKey, setHydratedQueryKey] = (0, react_1.useState)(enabled ? null : hydrationKey);
    const isHydrated = !enabled || hydratedQueryKey === hydrationKey || writtenQueryKeyRef.current === hydrationKey;
    (0, react_1.useEffect)(() => {
        if (!enabled) {
            setHydratedQueryKey(hydrationKey);
            return;
        }
        if (pendingWriteQueryKeyRef.current) {
            if (pendingWriteQueryKeyRef.current === hydrationKey) {
                pendingWriteQueryKeyRef.current = undefined;
                pendingWriteSourceQueryKeyRef.current = undefined;
                setHydratedQueryKey(hydrationKey);
                return;
            }
            if (pendingWriteSourceQueryKeyRef.current === hydrationKey) {
                setHydratedQueryKey(hydrationKey);
                return;
            }
            pendingWriteQueryKeyRef.current = undefined;
            pendingWriteSourceQueryKeyRef.current = undefined;
        }
        if (writtenQueryKeyRef.current === hydrationKey) {
            setHydratedQueryKey(hydrationKey);
            return;
        }
        const decodedQueryState = (0, view_query_params_utils_1.decodeViewQueryState)(encodedQueryState);
        if (!decodedQueryState || (0, ramda_1.equals)(decodedQueryState, hydratedStateRef.current)) {
            setHydratedQueryKey(hydrationKey);
            return;
        }
        if (shouldHydrate && !shouldHydrate(decodedQueryState)) {
            setHydratedQueryKey(hydrationKey);
            return;
        }
        hydratedStateRef.current = decodedQueryState;
        pendingHydrationRef.current = decodedQueryState;
        onHydrate(decodedQueryState);
        setHydratedQueryKey(hydrationKey);
    }, [enabled, encodedQueryState, hydrationKey, onHydrate, shouldHydrate]);
    (0, react_1.useEffect)(() => {
        if (!enabled || !isHydrated)
            return;
        const decodedQueryState = (0, view_query_params_utils_1.decodeViewQueryState)(encodedQueryState);
        const normalizedEncodedQueryState = decodedQueryState ? (0, view_query_params_utils_1.encodeViewQueryState)(decodedQueryState, defaults) : undefined;
        if (pendingHydrationRef.current
            && (0, ramda_1.equals)(decodedQueryState, pendingHydrationRef.current)
            && normalizedEncodedQueryState !== encodedViewState) {
            pendingHydrationRef.current = undefined;
            return;
        }
        pendingHydrationRef.current = undefined;
        if (normalizedEncodedQueryState === encodedViewState)
            return;
        const nextSearch = getSearchWithViewState(search, queryKey, encodedViewState);
        if (nextSearch === search)
            return;
        writtenQueryKeyRef.current = encodedViewState !== null && encodedViewState !== void 0 ? encodedViewState : '';
        pendingWriteQueryKeyRef.current = encodedViewState !== null && encodedViewState !== void 0 ? encodedViewState : '';
        pendingWriteSourceQueryKeyRef.current = hydrationKey;
        navigate({ hash, pathname, search: nextSearch }, { replace: true });
    }, [defaults, enabled, encodedQueryState, encodedViewState, hash, hydrationKey, isHydrated, navigate, pathname, queryKey, search]);
    return isHydrated;
};
exports.useViewQueryParamsSync = useViewQueryParamsSync;
